import {
  ActivityIndicator,
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import themes from '_/constants/themes';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { DateRangePicker, Header, Text } from '_/components';
import { useNavigation } from '@react-navigation/native';
import { CredentialingApprovalsProps } from '_/routes/types';
import { useCallback, useState } from 'react';
import { fontFamily } from '_/config/theme';
import { translate } from '_/locales';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useVisitsContext } from '_/hooks/VisitsProvider';
import { VisitItem } from '_/components/VisitItem';
import { queryClient } from '_/services/queryClient';
import { VisitAuthorizationStatusType, VisitsModel } from '@w3lcome/types';
import { showMessage } from 'react-native-flash-message';
import { Feather } from '@expo/vector-icons';
import Button from '_/components/Button';
import moment from 'moment';

const DEFAULT_LIMIT = 100;

export default function CredentialingApprovals() {
  const { goBack } = useNavigation<CredentialingApprovalsProps>();

  const [selectedStartDate, setSelectedStartDate] = useState<Date>(
    moment().startOf('day')?.toDate()
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(moment().endOf('day')?.toDate());
  const [reachedEnd, setReachedEnd] = useState(false);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState<VisitAuthorizationStatusType[]>([]);
  const [pendingStatusFilter, setPendingStatusFilter] = useState<VisitAuthorizationStatusType[]>(
    []
  );

  const STATUS_OPTIONS = [
    { label: translate('personalInvites.AWAITING'), value: VisitAuthorizationStatusType.AWAITING },
    { label: translate('personalInvites.ALLOWED'), value: VisitAuthorizationStatusType.ALLOWED },
    // { label: translate('personalInvites.DENIED'), value: VisitAuthorizationStatusType.DENIED },
  ];

  const { getCredentialingApprovals } = useVisitsContext();

  function handleChangeDates(startDate: Date, endDate: Date) {
    setSelectedEndDate(endDate);
    setSelectedStartDate(startDate);
  }

  const { data, isLoading } = useQuery({
    queryKey: [
      'credentialingApprovals',
      selectedStartDate?.toISOString(),
      selectedEndDate?.toISOString(),
      statusFilter,
    ],
    enabled: !!selectedStartDate && !!selectedEndDate,
    staleTime: 1000 * 60 * 1,
    queryFn: async () =>
      getCredentialingApprovals({
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        statusFilter,
      }),
  });

  const { mutate: addApproval } = useMutation({
    mutationFn: async (newApproval: VisitsModel) => {
      return newApproval;
    },
    onSuccess: (newApproval: VisitsModel) => {
      queryClient.setQueryData(
        [
          'credentialingApprovals',
          selectedStartDate?.toISOString(),
          selectedEndDate?.toISOString(),
          statusFilter,
        ],
        (oldData: any | undefined) => {
          if (!oldData || oldData?.data?.length === 0) return [newApproval];

          const existsIndex = oldData?.data?.findIndex((item: any) => item.id === newApproval.id);

          if (existsIndex !== -1) {
            const updated = [...oldData?.data];
            updated[existsIndex] = newApproval;

            return {
              ...oldData,
              data: updated,
            };
          }

          return {
            ...oldData,
            data: [newApproval, ...oldData?.data],
          };
        }
      );
    },
  });

  async function handleNextPagination(skip: number) {
    if (data && data?.data?.length < DEFAULT_LIMIT) return;

    const response = await getCredentialingApprovals({
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      limit: DEFAULT_LIMIT,
      skip,
    });

    queryClient.setQueryData(
      [
        'credentialingApprovals',
        selectedStartDate?.toISOString(),
        selectedEndDate?.toISOString(),
        statusFilter,
      ],
      (oldData: any) => {
        if (!response) return oldData;

        const updatedList = [...(oldData?.data || []), ...response.data];

        const updatedTotal = Number(oldData.total + response.total);

        if (response.data.length < DEFAULT_LIMIT) {
          setReachedEnd(true);
        }

        return {
          ...oldData,
          data: updatedList,
          total: updatedTotal,
        };
      }
    );
  }

  const loadMore = useCallback(async () => {
    try {
      if (!!data?.data?.length && !reachedEnd) {
        await handleNextPagination(data?.data?.length);
      }
    } catch (error) {
      showMessage({
        message: translate('personalMeeting.errorTryAgainLater'),
        type: 'danger',
      });
    }
  }, [data, reachedEnd]);

  function toggleFilter(value: VisitAuthorizationStatusType) {
    setPendingStatusFilter((prev) =>
      prev?.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }

  function render() {
    if (isLoading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={themes.colors.primary} />
        </View>
      );
    }

    return (
      <FlatList
        onEndReached={loadMore}
        onEndReachedThreshold={0.2}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              Selecione um intervalo de datas para vizualizar as credenciados ativos
            </Text>
            <Image source={themes.images.bigCalendar} style={styles.emptyImage} />
          </View>
        }
        style={styles.content}
        contentContainerStyle={{ paddingBottom: 80 }}
        data={data?.data}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <VisitItem
            key={item.id}
            visit={item as any}
            isCredentialed
            getUpdatedItem={addApproval as any}
          />
        )}
      />
    );
  }

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Header handleBack={() => goBack()} title={translate('credentialingApprovals')} />
        <View style={styles.content}>
          <View style={styles.datePickerContainer}>
            <DateRangePicker
              selectedEndDate={selectedEndDate}
              selectedStartDate={selectedStartDate}
              handleChangeDates={handleChangeDates}
            />
            <TouchableOpacity
              style={styles.filterButton}
              onPress={() => setIsFilterModalVisible(true)}
            >
              <Feather name="filter" size={24} color={themes.colors.primary} />
              {statusFilter.length > 0 && (
                <View
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    backgroundColor: themes.colors.success,
                    borderRadius: 8,
                    minWidth: 16,
                    height: 16,
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingHorizontal: 4,
                  }}
                >
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: 'bold' }}>
                    {statusFilter.length}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          </View>

          {render()}
        </View>

        {isFilterModalVisible && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>{translate('filterByStatus')}</Text>

              {STATUS_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={styles.checkboxContainer}
                  onPress={() => toggleFilter(option.value)}
                >
                  <Feather
                    name={pendingStatusFilter?.includes(option.value) ? 'check-square' : 'square'}
                    size={24}
                    color={themes.colors.primary}
                  />
                  <Text style={styles.checkboxLabel}>{option.label}</Text>
                </TouchableOpacity>
              ))}

              <View
                style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 24 }}
              >
                <Button
                  onPress={() => setIsFilterModalVisible(false)}
                  buttonStyles={{ backgroundColor: themes.colors.danger }}
                >
                  <Text uppercase={false} style={styles.applyButtonText}>
                    {translate('general.cancel')}
                  </Text>
                </Button>

                <Button
                  onPress={() => {
                    setStatusFilter(pendingStatusFilter);
                    setIsFilterModalVisible(false);
                  }}
                  buttonStyles={{ backgroundColor: themes.colors.lightGreen }}
                >
                  <Text uppercase={false} style={styles.applyButtonText}>
                    {translate('general.filter')}
                  </Text>
                </Button>
              </View>
            </View>
          </View>
        )}
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.colors.white,
    flex: 1,
  },
  content: {
    flex: 1,
  },
  rowContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  datePickerContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    height: 50,
    marginTop: 16,
    gap: 16,
  },
  title: {
    color: themes.colors.darkSecondary,
    fontSize: 24,
    marginRight: 6,
  },
  lengthText: {
    color: themes.colors.darkSecondary,
    fontSize: 14,
    marginRight: 4,
  },
  expandButtonContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 8,
  },
  expandButtonText: {
    color: themes.colors.darkQuaternary,
    fontFamily: fontFamily.medium,
    fontSize: 12,
    marginRight: 8,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    paddingVertical: 56,
    paddingHorizontal: 12,
  },
  emptyImage: {
    resizeMode: 'contain',
    flex: 1,
  },
  emptyText: {
    color: themes.colors.darkQuaternary,
    fontFamily: fontFamily.medium,
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  icons: {
    fontSize: 12,
    color: themes.colors.darkSecondary,
  },
  iconChevron: {
    fontSize: 14,
    color: themes.colors.darkQuaternary,
  },
  filterButton: {
    borderWidth: 1,
    borderColor: themes.colors.lightPrimary,
    height: 50,
    width: 50,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 99,
  },
  modalContent: {
    backgroundColor: themes.colors.white,
    borderRadius: 8,
    padding: 20,
    width: '80%',
  },
  modalTitle: {
    color: themes.colors.darkPrimary,
    fontFamily: fontFamily.bold,
    fontSize: 18,
    marginBottom: 16,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkboxLabel: {
    marginLeft: 12,
    fontSize: 16,
    fontFamily: fontFamily.medium,
  },
  applyButton: {
    marginTop: 16,
    backgroundColor: themes.colors.primary,
    padding: 12,
    borderRadius: 4,
    alignItems: 'center',
  },
  applyButtonText: {
    color: themes.colors.lightPrimary,
    fontFamily: fontFamily.medium,
    fontSize: 16,
  },
});
